import { supabase } from '@/shared/lib/supabase';

/**
 * Provisioning queue read service. Reads the `v_admin_provisioning_queue`
 * view (staff-only via RLS on `provisioning_job`).
 *
 * The view excludes `complete` jobs and joins customer_profile.full_name +
 * email so the UI shows context without follow-up queries. Order is priority
 * DESC, then scheduled_for ASC.
 */

export type ProvisioningStatus =
  | 'pending'
  | 'running'
  | 'failed_retryable'
  | 'failed_terminal'
  | 'cancelled';

export type ProvisioningTargetKind = 'whm' | 'domains-co-za' | 'email' | 'system';

export interface ProvisioningJobRow {
  id: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  step_kind: string;
  target_kind: ProvisioningTargetKind;
  target_ref: string | null;
  status: ProvisioningStatus;
  priority: number;
  attempt_count: number;
  max_attempts: number;
  error_code: string | null;
  error_message: string | null;
  scheduled_for: string;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  compensates_job_id: string | null;
}

export interface ProvisioningJobDetail extends ProvisioningJobRow {
  payload: unknown;
  last_response_payload: unknown;
}

export interface ProvisioningQueueFilter {
  status?: ProvisioningStatus | 'all';
  step_kind?: string;
  customer_id?: string;
}

const VIEW_COLS =
  'id, customer_id, customer_name, customer_email, step_kind, target_kind, target_ref, status, priority, attempt_count, max_attempts, error_code, error_message, scheduled_for, started_at, finished_at, created_at, compensates_job_id';

const DETAIL_COLS =
  'id, customer_id, step_kind, target_kind, target_ref, status, priority, attempt_count, max_attempts, error_code, error_message, scheduled_for, started_at, finished_at, created_at, compensates_job_id, payload, last_response_payload';

export const provisioningService = {
  async listQueue(
    filter: ProvisioningQueueFilter = {}
  ): Promise<ProvisioningJobRow[]> {
    let q = supabase.from('v_admin_provisioning_queue').select(VIEW_COLS);
    if (filter.status && filter.status !== 'all') {
      q = q.eq('status', filter.status);
    }
    if (filter.step_kind) q = q.eq('step_kind', filter.step_kind);
    if (filter.customer_id) q = q.eq('customer_id', filter.customer_id);

    const { data, error } = await q;
    if (error) {
      throw new Error(`Could not load provisioning queue: ${error.message}`);
    }
    return (data ?? []) as ProvisioningJobRow[];
  },

  /** Fetch full job payload — used by the row drawer. */
  async getById(id: string): Promise<ProvisioningJobDetail | null> {
    const { data, error } = await supabase
      .from('provisioning_job')
      .select(DETAIL_COLS)
      .eq('id', id)
      .maybeSingle();
    if (error) {
      throw new Error(`Could not load job: ${error.message}`);
    }
    if (!data) return null;
    const r = data as Omit<
      ProvisioningJobDetail,
      'customer_name' | 'customer_email'
    >;
    return {
      ...r,
      customer_name: null,
      customer_email: null,
    };
  },

  /**
   * Retry a failed job (Slice 1, 2026-09-10 — AR-S7).
   *
   * Calls the `provisioning-job-retry` Edge Function, which INSERTS a fresh
   * job carrying the original's step/target/payload and cancels the original
   * as `retried_as:<id>`. It never resets a status: every BEFORE INSERT guard
   * (removeacct policy + circuit breaker, arrangement hold) re-fires on the
   * new row. A refusal comes back as 409 `retry_refused_by_guard` and is
   * surfaced verbatim — that is the answer, not an outage.
   */
  async retry(jobId: string): Promise<ProvisioningRetryResponse> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke<
      ProvisioningRetryResponse | { code: string; message: string }
    >('provisioning-job-retry', {
      body: { job_id: jobId },
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    if (error) {
      const ctx = (error as unknown as { context?: { body?: string } }).context;
      if (ctx?.body) {
        try {
          const parsed = JSON.parse(ctx.body) as { code?: string; message?: string };
          throw new Error(parsed.message || parsed.code || error.message);
        } catch (e) {
          if (e instanceof Error && e.message !== error.message) throw e;
        }
      }
      throw new Error(error.message);
    }
    if (data && typeof data === 'object' && 'code' in data) {
      throw new Error((data as { message?: string; code: string }).message || data.code);
    }
    return data as ProvisioningRetryResponse;
  },
};

export interface ProvisioningRetryResponse {
  ok: true;
  original_job_id: string;
  new_job_id: string;
  step_kind: string;
  target_ref: string | null;
}
